<script lang="ts">
	import { trackArticleCompleted } from '../../utils/trackArticleCompleted/trackArticleCompleted';
	import type { ArticleReadSection } from '../../utils/trackArticleRead/trackArticleRead';
	import { trackArticleReadTime } from '../../utils/trackArticleReadTime/trackArticleReadTime';
	import {
		createActiveReadTimer,
		type ReadTimeMilestone,
	} from '$global/utils/createActiveReadTimer/createActiveReadTimer';

	interface Props {
		articleId: string;
		section: ArticleReadSection;
		// How close to the end of the body counts as completed, in pixels. Article
		// bodies are followed by furniture few readers scroll through — tags and
		// the newsletter widget on articles, footer/unsubscribe on newsletters —
		// so the sentinel counts as reached once within this distance below the
		// viewport. Newsletters need the larger allowance.
		completionThreshold?: number;
		// Rendered at the end of the article body; when it scrolls into view the
		// reader has reached the end and the article counts as completed.
		children?: import('svelte').Snippet;
	}

	let {
		articleId,
		section,
		completionThreshold = 0,
		children,
	}: Props = $props();

	let completionSentinel: HTMLElement | undefined = $state();

	// The read-time and completion signals are per-article: an in-place article
	// swap (client-side navigation reusing this component) has to restart both.
	$effect(() => {
		if (!articleId) {
			return;
		}

		const { stop } = createActiveReadTimer({
			onMilestone: (milestone: ReadTimeMilestone) => {
				trackArticleReadTime({ articleId, section, milestone });
			},
		});

		return stop;
	});

	$effect(() => {
		if (!articleId || !completionSentinel) {
			return;
		}

		let hasTrackedCompletion = false;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && !hasTrackedCompletion) {
						hasTrackedCompletion = true;

						trackArticleCompleted({ articleId, section });

						// One completion per article — stop observing rather than
						// re-checking the guard on every subsequent scroll.
						observer.disconnect();
					}
				});
			},
			// Grows the viewport downwards so the sentinel counts as reached while
			// it is still below the fold, rather than only once scrolled to.
			{ rootMargin: `0px 0px ${completionThreshold}px 0px` }
		);

		observer.observe(completionSentinel);

		return () => {
			observer.disconnect();
		};
	});
</script>

{@render children?.()}

<div bind:this={completionSentinel} aria-hidden="true"></div>
